import { Controller } from "@hotwired/stimulus"

/**
 * CFA Poller Controller
 *
 * Polls the Cash Flow Analysis status endpoint while analysis is in progress.
 * Automatically stops polling when analysis completes or fails.
 *
 * @example
 * <div data-controller="cfa-poller"
 *      data-cfa-poller-application-id-value="123"
 *      data-cfa-poller-status-url-value="/application/123/cfa/status">
 *   Processing...
 * </div>
 */
export default class extends Controller {
  static values = {
    applicationId: String,
    statusUrl: String,
    interval: { type: Number, default: 3000 } // Poll every 3 seconds
  }

  connect() {
    console.log("[CFA Poller] Connected for application:", this.applicationIdValue)
    this.startPolling()
  }

  disconnect() {
    console.log("[CFA Poller] Disconnecting")
    this.stopPolling()
  }

  startPolling() {
    if (!this.hasStatusUrlValue) {
      console.warn("[CFA Poller] No status URL configured")
      return
    }

    // Start polling immediately
    this.checkStatus()

    // Then poll at intervals
    this.pollTimer = setInterval(() => {
      this.checkStatus()
    }, this.intervalValue)

    console.log("[CFA Poller] Started polling every", this.intervalValue, "ms")
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
      console.log("[CFA Poller] Stopped polling")
    }
  }

  async checkStatus() {
    if (!this.hasStatusUrlValue) {
      return
    }

    try {
      const response = await fetch(this.statusUrlValue, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })

      if (response.ok) {
        const data = await response.json()
        console.log("[CFA Poller] Status:", data.status)

        // Stop polling if analysis is complete or failed
        if (data.status === "completed" || data.status === "failed") {
          console.log("[CFA Poller] Analysis finished with status:", data.status)
          this.stopPolling()

          // Reload the page to show updated status
          // The Turbo Stream from the background job should have already updated the UI,
          // but this ensures we see the latest state
          setTimeout(() => {
            window.location.reload()
          }, 1000)
        }
      } else {
        console.error("[CFA Poller] Status check failed:", response.status)

        // Stop polling on repeated errors
        if (response.status === 404 || response.status === 403) {
          this.stopPolling()
        }
      }
    } catch (error) {
      console.error("[CFA Poller] Error checking status:", error)
      // Continue polling despite errors - might be temporary network issue
    }
  }
}
